/**
 * Shim for `@datadog/mobile-react-native`, backed by the Datadog Browser SDKs
 * (`@datadog/browser-rum` and `@datadog/browser-logs`). RUM/Logs are only
 * initialised when a browser client token is configured; otherwise the API
 * degrades to console logging so the app keeps working locally.
 */
import React, { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

/* ------------------------------ Enums ------------------------------------ */

export enum SdkVerbosity {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export enum UploadFrequency {
  RARE = 'RARE',
  AVERAGE = 'AVERAGE',
  FREQUENT = 'FREQUENT',
}

export enum ErrorSource {
  NETWORK = 'network',
  SOURCE = 'source',
  CONSOLE = 'console',
  LOGGER = 'logger',
  AGENT = 'agent',
  CUSTOM = 'custom',
}

export enum RumActionType {
  TAP = 'tap',
  SCROLL = 'scroll',
  SWIPE = 'swipe',
  CLICK = 'click',
  CUSTOM = 'custom',
}

/* ----------------------- Provider configuration --------------------------- */

export class DatadogProviderConfiguration {
  serviceName?: string;
  nativeCrashReportEnabled = false;
  verbosity: SdkVerbosity = SdkVerbosity.INFO;
  site = 'US1';
  longTaskThresholdMs = 100;
  sessionSamplingRate = 100;
  uploadFrequency: UploadFrequency = UploadFrequency.AVERAGE;

  constructor(
    public clientToken: string,
    public env: string,
    public applicationId: string,
    public trackInteractions = true,
    public trackResources = true,
    public trackErrors = true,
  ) {}
}

let initialised = false;

function initialiseDatadog(config: DatadogProviderConfiguration) {
  if (initialised) return;
  const clientToken = config.clientToken || import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
  const applicationId = config.applicationId || import.meta.env.VITE_DD_RUM_APP_ID;
  if (!clientToken || !applicationId) {
    // No browser RUM credentials configured — stay in console-logging mode.
    return;
  }
  initialised = true;
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: config.serviceName ?? 'mobility',
    env: config.env,
    sessionSampleRate: config.sessionSamplingRate,
    sessionReplaySampleRate: 0,
    trackUserInteractions: config.trackInteractions,
    trackResources: config.trackResources,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
  datadogLogs.init({
    clientToken,
    site: 'datadoghq.com',
    service: config.serviceName ?? 'mobility',
    env: config.env,
    forwardErrorsToLogs: config.trackErrors,
    sessionSampleRate: 100,
    beforeSend: (event) => {
      const companyTags = Object.entries(event)
        .filter(([key, value]) => key.startsWith('xom_') &&
          ['string', 'boolean', 'number'].includes(typeof value))
        .map(([key, value]) => `${key}:${String(value)}`);
      event.ddtags = [event.ddtags, ...companyTags].filter(Boolean).join(',');
      return true;
    },
  });
}

/* ---------------------------- Provider ----------------------------------- */

export interface DatadogProviderProps {
  configuration: DatadogProviderConfiguration;
  onInitialization?: () => void | Promise<void>;
  children?: React.ReactNode;
}

export const DatadogProvider = function DatadogProvider({
  configuration,
  onInitialization,
  children,
}: DatadogProviderProps) {
  useEffect(() => {
    initialiseDatadog(configuration);
    onInitialization?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
};

/* ------------------------------ Logs ------------------------------------- */

function log(level: 'debug' | 'info' | 'warn' | 'error', message: string, context?: object) {
  if (initialised) {
    (datadogLogs.logger as any)[level](message, context);
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    (console as any)[level === 'debug' ? 'log' : level](`[DD] ${message}`, context ?? '');
  }
}

export const DdLogs = {
  debug: (message: string, context?: object) => log('debug', message, context),
  info: (message: string, context?: object) => log('info', message, context),
  warn: (message: string, context?: object) => log('warn', message, context),
  error: (message: string, context?: object) => log('error', message, context),
};

/* ------------------------------ RUM -------------------------------------- */

export const DdRum = {
  addAction: async (_type: RumActionType, name: string, context?: object) => {
    if (initialised) datadogRum.addAction(name, context);
  },
  addError: async (message: string, _source: ErrorSource, stack?: string, context?: object) => {
    if (initialised) datadogRum.addError(new Error(message), { stack, ...context });
    else if (import.meta.env.DEV) console.error(`[DD] ${message}`, context ?? '');
  },
  addTiming: async (name: string) => {
    if (initialised) datadogRum.addTiming(name);
  },
  startView: async (key: string, name?: string) => {
    if (initialised) datadogRum.startView({ name: name ?? key });
  },
  stopView: async () => {},
};

/* -------------------------- SDK utilities -------------------------------- */

export const DdSdkReactNative = {
  setAttributes: async (attributes: Record<string, unknown>) => {
    if (initialised) {
      datadogRum.setGlobalContext(attributes as any);
      datadogLogs.setGlobalContext(attributes);
    }
  },
  setUser: async (user: Record<string, unknown>) => {
    if (initialised) datadogRum.setUser(user as any);
  },
  clearUser: async () => {
    if (initialised) datadogRum.clearUser();
  },
};

export default {
  DatadogProvider,
  DatadogProviderConfiguration,
  DdLogs,
  DdRum,
  DdSdkReactNative,
  SdkVerbosity,
  UploadFrequency,
  ErrorSource,
  RumActionType,
};
